import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CheckCircle2 } from "lucide-react"

import { Button } from "@f2e/ui/components/base"

import {
  getFieldCurrentValue,
  subscribeFieldApplyValue,
  subscribeSuggestionApplyEvents,
} from "@/components/Copilot/applySuggestion/applySuggestionHandler"
import {
  buildApplyLineDiff,
  compactApplyLineDiff,
  hasApplyDiffChanges,
  isShortApplyText,
  prepareApplyDiffValue,
  type ApplyDiffLine,
} from "@/components/Copilot/applySuggestion/applyDiff"
import { diffTextHtml } from "@/lib/diffTextHtml"

export interface ApplySuggestionDiffProps {
  applyKey: string
  content: string
}

const TEXT_DIFF_CLASSES = [
  "[&_ins.diff-add]:rounded-sm [&_ins.diff-add]:bg-emerald-100 [&_ins.diff-add]:text-emerald-950 [&_ins.diff-add]:no-underline",
  "[&_del.diff-del]:rounded-sm [&_del.diff-del]:bg-red-100 [&_del.diff-del]:text-red-950 [&_del.diff-del]:line-through",
  "dark:[&_ins.diff-add]:bg-emerald-950/70 dark:[&_ins.diff-add]:text-emerald-100",
  "dark:[&_del.diff-del]:bg-red-950/70 dark:[&_del.diff-del]:text-red-100",
].join(" ")

const JsonDiffLine = ({ line }: { line: ApplyDiffLine }) => {
  if (line.type === "omitted") return null
  if (line.type === "added") {
    return (
      <ins className="block bg-emerald-100 text-emerald-950 no-underline dark:bg-emerald-950/70 dark:text-emerald-100">
        <span className="inline-block w-5 select-none text-center" aria-hidden>
          +
        </span>
        {line.text}
      </ins>
    )
  }
  if (line.type === "removed") {
    return (
      <del className="block bg-red-100 text-red-950 line-through dark:bg-red-950/70 dark:text-red-100">
        <span className="inline-block w-5 select-none text-center" aria-hidden>
          −
        </span>
        {line.text}
      </del>
    )
  }
  return (
    <span className="block">
      <span className="inline-block w-5 select-none" aria-hidden />
      {line.text}
    </span>
  )
}

const TextDiffLines = ({
  lines,
  omittedLabel,
}: {
  lines: ApplyDiffLine[]
  omittedLabel: (count: number) => string
}) => {
  const renderedLines = []

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    if (line.type === "omitted") {
      renderedLines.push(
        <span
          className="my-1 block border-y border-border/70 bg-muted/70 py-1 text-center text-xs text-muted-foreground"
          key={`omitted-${index}`}
        >
          {omittedLabel(line.count ?? 0)}
        </span>
      )
      continue
    }

    if (line.type === "removed") {
      const removed: ApplyDiffLine[] = []
      while (lines[index]?.type === "removed") {
        removed.push(lines[index])
        index += 1
      }
      const added: ApplyDiffLine[] = []
      while (lines[index]?.type === "added") {
        added.push(lines[index])
        index += 1
      }
      index -= 1

      const pairedCount = Math.min(removed.length, added.length)
      for (let pair = 0; pair < pairedCount; pair += 1) {
        renderedLines.push(
          <span
            className="block min-h-5"
            dangerouslySetInnerHTML={{
              __html: diffTextHtml(
                removed[pair].text ?? "",
                added[pair].text ?? ""
              ),
            }}
            key={`changed-${index}-${pair}`}
          />
        )
      }
      removed.slice(pairedCount).forEach((removedLine, offset) => {
        renderedLines.push(
          <del
            className="block min-h-5 bg-red-100 text-red-950 dark:bg-red-950/70 dark:text-red-100"
            key={`removed-${index}-${offset}`}
          >
            {removedLine.text}
          </del>
        )
      })
      added.slice(pairedCount).forEach((addedLine, offset) => {
        renderedLines.push(
          <ins
            className="block min-h-5 bg-emerald-100 text-emerald-950 no-underline dark:bg-emerald-950/70 dark:text-emerald-100"
            key={`added-${index}-${offset}`}
          >
            {addedLine.text}
          </ins>
        )
      })
      continue
    }

    if (line.type === "added") {
      renderedLines.push(
        <ins
          className="block min-h-5 bg-emerald-100 text-emerald-950 no-underline dark:bg-emerald-950/70 dark:text-emerald-100"
          key={`added-${index}`}
        >
          {line.text}
        </ins>
      )
      continue
    }

    renderedLines.push(
      <span className="block min-h-5" key={`context-${index}`}>
        {line.text}
      </span>
    )
  }

  return renderedLines
}

export const ApplySuggestionDiff = ({
  applyKey,
  content,
}: ApplySuggestionDiffProps) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [revision, setRevision] = useState(0)
  const [isApplied, setIsApplied] = useState(false)
  const frozenDiffRef = useRef<ReturnType<typeof prepareApplyDiffValue> | null>(
    null
  )
  const refresh = useCallback(() => setRevision((value) => value + 1), [])

  useEffect(
    () => subscribeFieldApplyValue(applyKey, refresh),
    [applyKey, refresh]
  )

  const liveDiff = useMemo(() => {
    void revision
    return prepareApplyDiffValue(getFieldCurrentValue(applyKey), content)
  }, [applyKey, content, revision])

  useEffect(() => {
    setIsApplied(false)
    frozenDiffRef.current = null
  }, [applyKey, content])

  useEffect(
    () =>
      subscribeSuggestionApplyEvents((event) => {
        if (!event.requestedKeys.includes(applyKey)) return

        if (event.phase === "start") {
          const currentDiff = prepareApplyDiffValue(
            getFieldCurrentValue(applyKey),
            content
          )
          if (hasApplyDiffChanges(currentDiff)) {
            frozenDiffRef.current = currentDiff
          }
          return
        }

        if (!event.appliedKeys.includes(applyKey)) return

        const appliedDiff = prepareApplyDiffValue(
          getFieldCurrentValue(applyKey),
          content
        )
        setIsApplied(!hasApplyDiffChanges(appliedDiff))
      }),
    [applyKey, content]
  )

  const diff =
    isApplied && frozenDiffRef.current ? frozenDiffRef.current : liveDiff

  const allLines = useMemo(
    () => buildApplyLineDiff(diff.previous, diff.proposed),
    [diff.previous, diff.proposed]
  )
  const compactLines = useMemo(
    () => compactApplyLineDiff(allLines, 2),
    [allLines]
  )

  if (diff.kind === "unavailable") {
    return (
      <pre className="not-prose overflow-x-auto whitespace-pre-wrap break-words bg-zinc-950 p-4 text-sm leading-relaxed text-zinc-100">
        {diff.proposed}
      </pre>
    )
  }

  const hasChanges = hasApplyDiffChanges(diff)
  const isShortText = isShortApplyText(diff)
  const visibleLines = expanded ? allLines : compactLines
  const canExpand = compactLines.some((line) => line.type === "omitted")

  return (
    <section
      className="not-prose border-x border-b border-border/70 bg-background text-foreground"
      aria-label={t("common.copilot.applyChangesPreview")}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border/70 px-3 py-2 text-xs text-muted-foreground">
        <span>{t("common.copilot.applyCurrentToSuggested")}</span>
        {isApplied ? (
          <span className="inline-flex shrink-0 items-center gap-1 font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t("common.copilot.appliedToDraft")}
          </span>
        ) : null}
      </div>

      {!hasChanges ? (
        <p className="m-0 px-3 py-4 text-sm text-muted-foreground">
          {t("common.copilot.applyNoChanges")}
        </p>
      ) : isShortText ? (
        <div className="divide-y divide-border/70 text-sm">
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 px-3 py-2.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t("common.copilot.applyCurrentValue")}
            </span>
            <del className="break-words text-red-950 dark:text-red-100">
              {diff.previous || t("common.copilot.applyEmptyValue")}
            </del>
          </div>
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 bg-emerald-50 px-3 py-2.5 dark:bg-emerald-950/40">
            <span className="text-xs font-medium text-muted-foreground">
              {t("common.copilot.applySuggestedValue")}
            </span>
            <ins className="break-words text-emerald-950 no-underline dark:text-emerald-100">
              {diff.proposed || t("common.copilot.applyEmptyValue")}
            </ins>
          </div>
        </div>
      ) : diff.kind === "json" ? (
        <pre
          className="m-0 overflow-x-auto py-2 font-mono text-xs leading-5"
          aria-label={t("common.copilot.applyJsonChanges")}
        >
          {visibleLines.map((line, index) =>
            line.type === "omitted" ? (
              <span
                className="my-1 block border-y border-border/70 bg-muted/70 py-1 text-center font-sans text-xs text-muted-foreground"
                key={`omitted-${index}`}
              >
                {t("common.copilot.applyUnchangedLinesOmitted", {
                  count: line.count,
                })}
              </span>
            ) : (
              <JsonDiffLine line={line} key={`${line.type}-${index}`} />
            )
          )}
        </pre>
      ) : (
        <div
          className={`m-0 whitespace-pre-wrap break-words px-3 py-3 font-sans text-sm leading-relaxed ${TEXT_DIFF_CLASSES}`}
        >
          <TextDiffLines
            lines={visibleLines}
            omittedLabel={(count) =>
              t("common.copilot.applyUnchangedLinesOmitted", { count })
            }
          />
        </div>
      )}

      {hasChanges && canExpand ? (
        <div className="flex justify-end border-t border-border/70 px-2 py-1.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded
              ? t("common.copilot.applyShowOnlyChanges")
              : t("common.copilot.applyViewFullChanges")}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

export default ApplySuggestionDiff
